import { tw } from "@careinfo/tailwind";
import {
  Download,
  File,
  FileArchive,
  FileAudio,
  FileCheck,
  FileImage,
  FileText,
  FileVideo,
  FileX,
  X,
} from "lucide-react";
import { useState } from "react";
import APIS from "../../../constant/Endpoint";
import { downloadApiDataGET } from "../../../helpers/AxiosInstance";

const isValidUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const isWebsiteLink = (url: string): boolean => {
  if (!isValidUrl(url)) return false;

  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const blockedExts = [
      "pdf",
      "doc",
      "docx",
      "txt",
      "zip",
      "rar",
      "7z",
      "mp3",
      "mp4",
      "avi",
      "exe",
      "json",
      "xlsx",
    ];

    if (blockedExts.some((ext) => pathname.endsWith(`.${ext}`))) {
      return false;
    }

    if (isImageUrl(url)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const isImageUrl = (url: string): boolean => {
  const pathname = url.split("/").pop()?.split("?")[0] || "";
  return /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(pathname);
};

const getFileNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split("/").pop() || "file");
  } catch {
    return "file";
  }
};

const getFileIconComponent = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
    case "doc":
    case "docx":
    case "txt":
      return <FileText className={tw`w-6 h-6 text-blue-600`} />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return <FileImage className={tw`w-6 h-6 text-green-600`} />;
    case "mp3":
    case "wav":
      return <FileAudio className={tw`w-6 h-6 text-purple-600`} />;
    case "mp4":
    case "avi":
      return <FileVideo className={tw`w-6 h-6 text-pink-600`} />;
    case "zip":
    case "rar":
    case "7z":
      return <FileArchive className={tw`w-6 h-6 text-yellow-600`} />;
    case "json":
      return <FileCheck className={tw`w-6 h-6 text-teal-600`} />;
    case "exe":
      return <FileX className={tw`w-6 h-6 text-red-600`} />;
    default:
      return <File className={tw`w-6 h-6 text-gray-500`} />;
  }
};

const Message = ({ message }: { message: string }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  if (!message) return null;

  if (!isValidUrl(message)) {
    return (
      <div
        className={tw`text-sm text-gray-800 whitespace-pre-wrap break-words`}
      >
        {message}
      </div>
    );
  }

  if (isWebsiteLink(message)) {
    return (
      <div className={tw`text-sm text-blue-600 underline break-words`}>
        <a href={message} target="_blank" rel="noopener noreferrer">
          {message}
        </a>
      </div>
    );
  }

  const fileName = getFileNameFromUrl(message);

  const downloadFile = async (fileUrl: string, fileName: string) => {
    setIsDownloading(true);
    const url = `${APIS.FILE_HANDLER}download/`;
    const formData = { url: fileUrl };

    try {
      const res = await downloadApiDataGET({
        url,
        fileName,
        formData,
      });

      if (res?.status === 200 && res?.data) {
        const blob = new Blob([res.data]);
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadUrl;

        const contentDisposition = res.headers?.["content-disposition"];
        let fileName = "downloaded-file";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) fileName = match[1];
        }

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={tw`text-sm text-gray-800 whitespace-pre-wrap break-words`}>
      {isImageUrl(message) ? (
        <>
          <img
            src={message}
            alt="preview"
            className={tw`max-w-xs max-h-60 rounded-md mt-1 cursor-pointer`}
            onClick={() => setPreviewImage(message)}
          />
          {previewImage && (
            <div
              className={tw`fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50`}
              onClick={() => setPreviewImage(null)}
            >
              <div
                className={tw`relative bg-white rounded-md p-4 max-w-full max-h-full`}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  className={tw`max-w-full max-h-[80vh] rounded-md`}
                />
                <button
                  onClick={() => downloadFile(previewImage, fileName)}
                  disabled={isDownloading}
                  className={tw`absolute top-2 right-12 bg-white p-2 rounded-full shadow hover:bg-gray-100 focus:outline-none`}
                >
                  <Download className={tw`w-5 h-5 `} />
                </button>
                <button
                  onClick={() => setPreviewImage(null)}
                  className={tw`absolute top-2 right-2 bg-white p-2 rounded-full shadow hover:bg-gray-100 focus:outline-none`}
                >
                  <X className={tw`w-5 h-5`} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div
          className={tw`flex items-center gap-2 p-2 border rounded-md w-fit transition`}
        >
          {getFileIconComponent(fileName)}
          <a
            href={message}
            target="_blank"
            rel="noopener noreferrer"
            className={tw`underline break-all`}
          >
            {fileName}
          </a>
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadFile(message, fileName);
            }}
            disabled={isDownloading || false}
            className={tw`focus:outline-none text-gray-500 hover:text-gray-800`}
          >
            <Download className={tw`w-5 h-5`} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Message;
